import { useInterval } from 'app/hooks/useInterval'
import {
  authSelector,
  AuthState,
  doLogout,
  isLoggedIn,
  resetRedirect
} from 'features/auth/authSlice'
import { useDispatch, useSelector } from 'react-redux'
import { Redirect, useLocation } from 'react-router-dom'
import React, { ReactNode, useEffect } from 'react'
import { getLastActivity } from 'utils/auth'

function useActivity(timeoutMs: number, checkIntervalMs: number) {
  const dispatch = useDispatch()
  const isAuth = useSelector(isLoggedIn)

  useInterval(() => {
    if (!isAuth) {
      return
    }

    const last = getLastActivity()
    const delta = last !== null ? Date.now() - last.getTime() : null

    if (delta === null || delta > timeoutMs) {
      dispatch(doLogout({ redirectTo: '/inactive' }))
      return
    }
  }, checkIntervalMs)
}

type ActivityTimeoutProps = {
  /** Timeout for logging out the user in miliseconds */
  timeoutMs: number
  /** Interval to perform the user inactivity check in miliseconds */
  checkIntervalMs: number
  /** Enabling or disabling the feature */
  when: boolean
  children: ReactNode
}

function ActivityTimeoutEnabled(props: Omit<ActivityTimeoutProps, 'when'>) {
  const { timeoutMs, checkIntervalMs, children } = props
  const auth: AuthState = useSelector(authSelector)
  const location = useLocation()
  const dispatch = useDispatch()

  useActivity(timeoutMs, checkIntervalMs)

  const isAtLocation = location.pathname === auth.redirectTo

  useEffect(() => {
    if (auth.redirectTo != null && isAtLocation) {
      dispatch(resetRedirect())
    }
  }, [dispatch, isAtLocation, auth.redirectTo])

  if (auth.redirectTo != null && !isAtLocation) {
    return <Redirect to={auth.redirectTo} />
  }

  return <>{children}</>
}

function ActivityTimeout(props: ActivityTimeoutProps) {
  const { when, ...rest } = props
  if (!when) {
    return <>{rest.children}</>
  }
  return <ActivityTimeoutEnabled {...props} />
}

export default ActivityTimeout
